import { Directive, ElementRef, HostListener } from '@angular/core';

@Directive({
  selector:
    "ion-input[type='text'],ion-textarea,ion-searchbar,textarea," +
    " ion-input:not([type]), input[type='text'], input:not([type]), [appCaretAtEnd]"
})
export class CaretAtEndDirective {
  private pendingCaretTimer: ReturnType<typeof setTimeout> | null = null;
  private pointerFocus = false;

  @HostListener('pointerdown')
  onPointerDown() {
    this.pointerFocus = true;
  }

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  @HostListener('focusin')
  onFocusIn() {
    this.clearPendingCaretTimer();

    const host = this.elementRef.nativeElement;
    const preservePointerCaret = host.classList.contains('image-name-input') && this.pointerFocus;
    this.pointerFocus = false;
    if (preservePointerCaret) {
      return;
    }

    void this.placeCaretAtEnd();
    this.pendingCaretTimer = setTimeout(() => {
      void this.placeCaretAtEnd();
    }, 0);
  }

  @HostListener('focusout')
  onFocusOut() {
    this.clearPendingCaretTimer();
    this.pointerFocus = false;
  }

  @HostListener('keydown.tab')
  onTabKeydown() {
    // Do not restore focus to this field when the user is navigating away.
    this.clearPendingCaretTimer();
  }

  private async placeCaretAtEnd() {
    const host = this.elementRef.nativeElement;

    if (host instanceof HTMLInputElement || host instanceof HTMLTextAreaElement) {
      this.setCaretAtEnd(host);
      return;
    }

    if (host.tagName?.toLowerCase() === 'ion-input') {
      const ionInputHost = host as HTMLElement & { getInputElement?: () => Promise<HTMLInputElement> };
      const innerInput = ionInputHost.getInputElement
        ? await ionInputHost.getInputElement()
        : (ionInputHost.shadowRoot?.querySelector('input') as HTMLInputElement | null);

      if (innerInput) {
        this.setCaretAtEnd(innerInput);
      }
      return;
    }

    if (host.tagName?.toLowerCase() === 'ion-textarea') {
      const ionTextareaHost = host as HTMLElement & {
        getInputElement?: () => Promise<HTMLTextAreaElement>;
      };
      const innerTextarea = ionTextareaHost.getInputElement
        ? await ionTextareaHost.getInputElement()
        : (ionTextareaHost.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement | null);

      if (innerTextarea) {
        this.setCaretAtEnd(innerTextarea);
      }
      return;
    }

    if (host.tagName?.toLowerCase() === 'ion-searchbar') {
      const ionSearchbarHost = host as HTMLElement & {
        getInputElement?: () => Promise<HTMLInputElement>;
      };
      const innerInput = ionSearchbarHost.getInputElement
        ? await ionSearchbarHost.getInputElement()
        : (ionSearchbarHost.shadowRoot?.querySelector('input') as HTMLInputElement | null);

      if (innerInput) {
        this.setCaretAtEnd(innerInput);
      }
    }
  }

  private setCaretAtEnd(inputElement: HTMLInputElement | HTMLTextAreaElement) {
    const valueLength = inputElement.value?.length || 0;
    try {
      inputElement.setSelectionRange(valueLength, valueLength);
    } catch {
      // Some input types or platforms may not allow selection ranges.
    }
  }

  private clearPendingCaretTimer() {
    if (this.pendingCaretTimer !== null) {
      clearTimeout(this.pendingCaretTimer);
      this.pendingCaretTimer = null;
    }
  }
}
